import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  appService,
  App,
  WorkspaceSubscriptionWithApp,
  useAppSubscription,
  useAuth,
  APP_CATEGORY_LABELS,
  APP_CATEGORY_ORDER,
  AppCategory,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@aaas/shared';
import { Loader2, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppCard, AppCardState, MarketplaceApp } from './marketplace/AppCard';

interface AppDashboardProps {
  workspaceId: string;
  userRole: 'owner' | 'member';
  workspaceName: string;
}

interface MarketplaceEntry {
  app: MarketplaceApp;
  state: AppCardState;
  category: AppCategory;
}

export const AppDashboard: React.FC<AppDashboardProps> = ({
  workspaceId,
  userRole,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const [allApps, setAllApps] = useState<App[]>([]);
  const [subscribedApps, setSubscribedApps] = useState<WorkspaceSubscriptionWithApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { triggerRefresh } = useAppSubscription();

  const canManageApps = userRole === 'owner';
  const canAssignSeats = canManageApps;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const idToken = await user.getIdToken();
      const [apps, subscriptions] = await Promise.all([
        appService.getApps(idToken, workspaceId),
        appService.getWorkspaceSubscriptions(workspaceId, idToken),
      ]);

      setAllApps(apps);
      setSubscribedApps(subscriptions);
    } catch (err) {
      console.error('Failed to load apps data:', err);
      setError(t('appDashboard.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Compose a SubscriptionWithApp record locally from a marketplace app entry,
   * so we can optimistically update the UI without round-tripping to the
   * server (and re-rendering the whole grid).
   */
  const buildSubscriptionEntry = (
    app: MarketplaceApp,
    category: AppCategory,
    subscribedBy: string
  ): WorkspaceSubscriptionWithApp => {
    const now = new Date().toISOString();
    return {
      workspace_id: workspaceId,
      app_id: app.id,
      subscribed_by: subscribedBy,
      created_at: now,
      updated_at: now,
      app_slug: app.slug,
      app_name: app.name,
      app_description: app.description,
      app_is_beta: app.is_beta,
      app_category: category,
      app_version: app.version,
    };
  };

  const handleSubscribe = async (app: MarketplaceApp) => {
    if (!user) return;
    // Look up the app's category from the master list (allApps) so we can
    // build the optimistic subscription entry. Default to uncategorized
    // as a last resort — the next server refresh will correct it anyway.
    const sourceApp = allApps.find((a) => a.id === app.id);
    const category: AppCategory = sourceApp?.category ?? 'uncategorized';

    try {
      setActionLoading(app.id);
      const idToken = await user.getIdToken();
      // Backend returns the persisted subscription incl. the canonical
      // PortalAccount UUID for `subscribed_by` — use that instead of
      // user.uid (Firebase UID) to keep frontend state consistent with
      // what the next refresh would return.
      const subscription = await appService.subscribeWorkspaceToApp(
        workspaceId,
        app.id,
        idToken
      );
      setSubscribedApps((prev) => [
        ...prev,
        buildSubscriptionEntry(app, category, subscription.subscribed_by),
      ]);
      triggerRefresh();
    } catch (err) {
      console.error('Failed to subscribe to app:', err);
      setError(t('appDashboard.subscribeFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsubscribe = async (app: MarketplaceApp) => {
    if (!user) return;
    try {
      setActionLoading(app.id);
      const idToken = await user.getIdToken();
      await appService.unsubscribeWorkspaceFromApp(workspaceId, app.id, idToken);
      // Optimistic: drop the row locally.
      setSubscribedApps((prev) => prev.filter((s) => s.app_id !== app.id));
      triggerRefresh();
    } catch (err) {
      console.error('Failed to unsubscribe from app:', err);
      setError(t('appDashboard.unsubscribeFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpen = (app: MarketplaceApp) => {
    window.location.href = `/${app.slug}/`;
  };

  const handleAssignSeats = (app: MarketplaceApp) => {
    navigate(`/workspace/assign-seats?app=${app.id}`);
  };

  /**
   * Build a unified list of marketplace entries.
   *
   * The order is anchored to `allApps` (server-side ordering) so a card stays
   * in place when its state flips between `available` ↔ `subscribed`. We only
   * walk `subscribedApps` for IDs that are not in `allApps` (e.g. a member who
   * cannot see the master list), to ensure their subscribed apps still render.
   */
  const entries: MarketplaceEntry[] = useMemo(() => {
    const subscribedById = new Map(subscribedApps.map((s) => [s.app_id, s]));
    const allAppIds = new Set(allApps.map((a) => a.id));

    const list: MarketplaceEntry[] = [];

    for (const app of allApps) {
      const sub = subscribedById.get(app.id);
      if (sub) {
        list.push({
          app: {
            id: app.id,
            slug: app.slug,
            name: app.name,
            description: app.description,
            is_active: app.is_active,
            is_beta: app.is_beta,
            version: app.version,
          },
          state: 'subscribed',
          category: app.category,
        });
        continue;
      }

      // Hide non-subscribed apps from non-owners.
      if (!canManageApps) continue;

      list.push({
        app,
        state: app.is_active ? 'available' : 'coming-soon',
        category: app.category,
      });
    }

    // Surface subscribed apps that are not in the master list (members whose
    // `getApps()` response is filtered to seated apps only).
    for (const sub of subscribedApps) {
      if (allAppIds.has(sub.app_id)) continue;
      list.push({
        app: {
          id: sub.app_id,
          slug: sub.app_slug,
          name: sub.app_name,
          description: sub.app_description,
          is_active: true,
          is_beta: sub.app_is_beta,
          version: sub.app_version,
        },
        state: 'subscribed',
        category: sub.app_category,
      });
    }

    return list;
  }, [allApps, subscribedApps, canManageApps]);

  /**
   * Group entries by category and respect APP_CATEGORY_ORDER. Skip empty
   * buckets so the UI doesn't render section headers without cards.
   */
  const grouped = useMemo(() => {
    const buckets = new Map<AppCategory, MarketplaceEntry[]>();
    for (const entry of entries) {
      const list = buckets.get(entry.category) ?? [];
      list.push(entry);
      buckets.set(entry.category, list);
    }
    return APP_CATEGORY_ORDER.map((cat) => ({
      category: cat as AppCategory,
      entries: buckets.get(cat as AppCategory) ?? [],
    })).filter((g) => g.entries.length > 0);
  }, [entries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8 text-center">
          <div>
            <p className="mb-2 text-red-600">{error}</p>
            <Button variant="outline" onClick={loadData}>
              {t('appDashboard.tryAgain')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Member with no access
  if (entries.length === 0 && !canManageApps) {
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            <Package className="h-5 w-5" />
            {t('appDashboard.noAppsHeading')}
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center">
          <p className="mb-4 text-gray-600">{t('appDashboard.noAppsText1')}</p>
          <p className="text-sm text-gray-500">{t('appDashboard.noAppsText2')}</p>
        </CardContent>
      </Card>
    );
  }

  // Default selected tab = first non-empty category. Stays stable across
  // subscribe/unsubscribe because we only seed it on first render.
  const defaultTab = grouped[0]?.category;
  if (!defaultTab) {
    return null;
  }

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      {/* Wrapper enables horizontal scroll on narrow viewports without
          forcing tabs to wrap (which breaks the active underline). */}
      <div className="relative overflow-x-auto scrollbar-thin border-b border-gray-200">
        <TabsList className="h-auto w-max justify-start gap-1 bg-transparent p-0 rounded-none border-0">
          {grouped.map(({ category, entries: catEntries }) => {
            const subscribedCount = catEntries.filter((e) => e.state === 'subscribed').length;
            return (
              <TabsTrigger
                key={category}
                value={category}
                className="gap-2 whitespace-nowrap rounded-none border-b-2 border-transparent px-3 py-2 -mb-px text-gray-600 shadow-none data-[state=active]:bg-transparent data-[state=active]:border-blue-600 data-[state=active]:text-gray-900 data-[state=active]:shadow-none"
              >
                <span>{APP_CATEGORY_LABELS[category]}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-600 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
                  {subscribedCount}/{catEntries.length}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>

      {grouped.map(({ category, entries: catEntries }) => (
        <TabsContent key={category} value={category} className="mt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {catEntries.map((entry) => (
              <AppCard
                key={entry.app.id}
                app={entry.app}
                state={entry.state}
                busy={actionLoading === entry.app.id}
                canAssignSeats={canAssignSeats}
                canManage={canManageApps}
                onOpen={handleOpen}
                onSubscribe={handleSubscribe}
                onUnsubscribe={handleUnsubscribe}
                onAssignSeats={handleAssignSeats}
              />
            ))}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
};
